import {
  AutomationFailureResponseStatus,
  ResolveAutomationFailureRequestResolution,
  type AutomationFailureResponse,
} from '@urbanx/agx.support.client';
import { ActionMenu, AgxButton, AgxSpacer, Icon_Chevron } from '@urbanx/agx.ui-components2';
import { getAutomationResolutionLabel } from './automationFailurePresentation';

interface AutomationFailureDetailActionsProps {
  failure?: AutomationFailureResponse;
  isPending: boolean;
  isError: boolean;
  resolvePending: boolean;
  reopenPending: boolean;
  onCopyDiagnostics: () => void;
  onResolve: (resolution: ResolveAutomationFailureRequestResolution) => void;
  onReopen: () => void;
}

const resolutionActions = Object.values(ResolveAutomationFailureRequestResolution).map(
  (resolution) => ({
    action: resolution,
    children: getAutomationResolutionLabel(resolution),
  })
);

const AutomationFailureDetailActions = ({
  failure,
  isPending,
  isError,
  resolvePending,
  reopenPending,
  onCopyDiagnostics,
  onResolve,
  onReopen,
}: AutomationFailureDetailActionsProps) => {
  if (!failure || isPending || isError) return null;

  const isResolved = failure.status === AutomationFailureResponseStatus.Resolved;
  const isUnresolved = failure.status === AutomationFailureResponseStatus.Unresolved;

  return (
    <AgxSpacer
      direction="horizontal"
      contentAlignment="center"
      contentJustification="space-between"
      gap="sm"
      fill
      wrap
    >
      <AgxButton
        id="automation-failure-copy-diagnostics"
        variant="naked"
        text="Copy diagnostics"
        onClick={onCopyDiagnostics}
      />
      <AgxSpacer direction="horizontal" contentAlignment="center" gap="sm">
        {isResolved && (
          <AgxButton
            id="automation-failure-reopen"
            variant="hollow"
            text={reopenPending ? 'Reopening...' : 'Reopen'}
            disabled={reopenPending}
            onClick={onReopen}
          />
        )}
        {isUnresolved && (
          <ActionMenu
            id="automation-failure-resolve-menu"
            className="automationResolveMenu"
            menuClassName="automationResolveMenuMenu"
            dataTestId="automation-failure-resolve"
            triggerAriaLabel="Choose how this error was resolved"
            trigger={
              <span className="agx-button-v2 primary medium automationResolveMenuTrigger">
                {resolvePending ? 'Resolving...' : 'Resolve'}
                <Icon_Chevron
                  color="var(--text-brand-main)"
                  height={16}
                  width={16}
                  direction="down"
                />
              </span>
            }
            actions={resolutionActions}
            onAction={onResolve}
          />
        )}
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default AutomationFailureDetailActions;
